import { Controller } from '@hotwired/stimulus'

// Drag&drop + lista de archivos seleccionados + validación cliente para
// la Pantalla 1 de subida de documentación de hipotecas (ENH-RNK-025).
//
// Targets:
//   - dropzone: la zona visual que recibe drag&drop
//   - fileInput: el <input type="file"> nativo (multiple), oculto vía CSS;
//     se abre programáticamente al hacer click sobre la zona
//   - fileList: <ul> donde se renderizan los archivos seleccionados
//   - error: <p> donde aparecen mensajes de validación cliente
//
// Values (vienen del HAML para no duplicar la lista en JS y backend):
//   - tamanoMaximoBytes: 10485760 (10 MB)
//   - mimePermitidos: array JSON con los MIME types aceptados
//
// Comportamiento:
//   - drop y change sucesivos acumulan archivos (no sobrescriben).
//   - Validación cliente antes de aceptar el archivo: tamaño y MIME.
//   - Si una validación falla, el archivo no se añade y se muestra error.
//
// Por qué necesitamos un buffer interno (`this.archivos`) en vez de leer
// `fileInput.files`: cuando el usuario abre el selector y elige nuevos
// archivos, el navegador REEMPLAZA `fileInput.files` con esa nueva
// selección antes de disparar `change` (los anteriores se pierden del
// DOM en ese mismo instante). Si en `archivosCambiados` reseteamos
// `value = ''` para permitir re-seleccionar un mismo archivo, también
// borramos los nuevos. La única forma de acumular sin perder ni los
// previos ni la posibilidad de re-seleccionar es mantener nosotros el
// estado en `this.archivos` y reescribir `fileInput.files` desde ahí.
export default class extends Controller {
  static targets = ['dropzone', 'fileInput', 'fileList', 'error']
  static values = {
    tamanoMaximoBytes: Number,
    mimePermitidos: Array
  }

  connect () {
    this.archivos = []
    this.actualizarLista()
  }

  prevenir (event) {
    event.preventDefault()
    event.stopPropagation()
  }

  arrastrar (event) {
    event.preventDefault()
    event.stopPropagation()
    this.dropzoneTarget.classList.add('is-arrastrando')
  }

  saliendo (event) {
    event.preventDefault()
    event.stopPropagation()
    this.dropzoneTarget.classList.remove('is-arrastrando')
  }

  soltar (event) {
    event.preventDefault()
    event.stopPropagation()
    this.dropzoneTarget.classList.remove('is-arrastrando')
    this.anadirArchivos(event.dataTransfer.files)
  }

  abrirSelector (event) {
    if (event.target.closest('button, a, .rnk-HipotecasDoc_FileItemRemove')) return
    this.fileInputTarget.click()
  }

  archivosCambiados () {
    // Guard de re-entrada: el `change` que re-emite `sincronizarInput` para
    // los demás controllers también nos llega a nosotros mismos. Sin este
    // corte se recursa change → archivosCambiados → anadirArchivos →
    // sincronizarInput → change… hasta RangeError (stack overflow) en cada
    // selección de archivo.
    if (this.sincronizando) return

    const seleccion = Array.from(this.fileInputTarget.files)
    // Filtrar archivos ya presentes (por nombre+tamaño) para no duplicar
    // si el usuario abre el selector y vuelve a elegir el mismo archivo.
    const nuevos = seleccion.filter(s =>
      !this.archivos.some(a => a.name === s.name && a.size === s.size)
    )
    this.anadirArchivos(nuevos)
  }

  eliminar (event) {
    const indice = parseInt(event.currentTarget.dataset.indice, 10)
    this.archivos = this.archivos.filter((_, i) => i !== indice)
    this.sincronizarInput()
    this.actualizarLista()
  }

  // Vacía completamente el buffer interno + input + UI. Lo usa el stepper
  // de titulares cuando se reduce el número de titulares: al quitar un T,
  // todo lo que subió se borra (Naiara 2026-05-13). Hacer solo
  // `input.value = ''` por fuera dejaba el buffer `this.archivos` lleno y,
  // si el usuario volvía a añadir el titular, reaparecían los archivos.
  reset () {
    this.archivos = []
    this.sincronizarInput()
    this.actualizarLista()
    this.mostrarError('')
  }

  // ---- privado ----

  anadirArchivos (lista) {
    const errores = []
    Array.from(lista).forEach(archivo => {
      const error = this.validar(archivo)
      if (error) {
        errores.push(`${archivo.name}: ${error}`)
      } else {
        this.archivos.push(archivo)
      }
    })
    this.sincronizarInput()
    this.mostrarError(errores.join(' — '))
    this.actualizarLista()
  }

  sincronizarInput () {
    const transfer = new DataTransfer()
    this.archivos.forEach(archivo => transfer.items.add(archivo))
    this.fileInputTarget.files = transfer.files
    // Reescribir `files` programáticamente NO dispara `change`. Lo
    // emitimos a mano (bubble) para que los demás controllers (acordeon,
    // titulares, submit) recalculen badges, dots y progreso al eliminar
    // un archivo o reorganizar la selección. El flag evita que nuestro
    // propio `archivosCambiados` re-procese este evento sintético (ver
    // guard de re-entrada arriba).
    this.sincronizando = true
    try {
      this.fileInputTarget.dispatchEvent(new Event('change', { bubbles: true }))
    } finally {
      this.sincronizando = false
    }
  }

  validar (archivo) {
    if (archivo.size > this.tamanoMaximoBytesValue) {
      const limiteMb = Math.round(this.tamanoMaximoBytesValue / 1024 / 1024)
      return `supera ${limiteMb} MB`
    }
    if (!this.mimePermitidosValue.includes(archivo.type)) {
      return 'formato no permitido (solo PDF, JPG o PNG)'
    }
    return null
  }

  actualizarLista () {
    if (!this.hasFileListTarget) return
    this.fileListTarget.innerHTML = ''
    this.archivos.forEach((archivo, i) => this.fileListTarget.appendChild(this.crearItem(archivo, i)))
    this.dropzoneTarget.classList.toggle('has-archivos', this.archivos.length > 0)
  }

  crearItem (archivo, indice) {
    const li = document.createElement('li')
    li.className = 'rnk-HipotecasDoc_FileItem'
    li.appendChild(this.crearSpan('rnk-HipotecasDoc_FileItemName', archivo.name))
    li.appendChild(this.crearSpan('rnk-HipotecasDoc_FileItemSize', this.formatearTamano(archivo.size)))
    li.appendChild(this.crearBotonEliminar(indice))
    return li
  }

  crearSpan (className, texto) {
    const span = document.createElement('span')
    span.className = className
    span.textContent = texto
    return span
  }

  crearBotonEliminar (indice) {
    const btn = document.createElement('button')
    btn.type = 'button'
    btn.className = 'rnk-HipotecasDoc_FileItemRemove'
    btn.dataset.action = 'hipotecas-doc-dropzone#eliminar'
    btn.dataset.indice = String(indice)
    btn.setAttribute('aria-label', 'Eliminar archivo')
    btn.textContent = '×'
    return btn
  }

  mostrarError (mensaje) {
    if (!this.hasErrorTarget) return
    this.errorTarget.textContent = mensaje
    this.errorTarget.classList.toggle('is-visible', mensaje.length > 0)
  }

  formatearTamano (bytes) {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`
  }
}
